import { DatabaseZap, ImagePlus, RefreshCw, Save, ScanSearch, Trash2, TriangleAlert } from 'lucide-react'
import { useState } from 'react'

import { ActionInfo, Button, ProductThumb, TextField } from '@krp/ui-kit'

import { ActionError } from '../../../shared/ActionError'
import { QueryState } from '../../../shared/QueryState'
import { preparePhoto } from '../../search/ui/preparePhoto'
import { TAG_HINTS, TAG_TITLES } from '../model/selectors'
import {
  TESTING_TAG_AXES,
  type TestingAccess,
  type TestingKind,
  type TestPhotoVm,
  type TestProductActions,
  type TestProduct,
  type TestProductDraft,
  type TestProductVm,
} from '../model/types'
import { StateBadge, TestingGate } from './TestingParts'

/**
 * Карточка тестового товара: поля, эталоны (что лежит в индексе) и кадры (чем ищем).
 * Из поиска по тексту — только поля: по ним ищут запросы, фото там не нужны (RP-FB34).
 */
export function TestProductScreen({
  access,
  vm,
  actions,
  kind = 'photo',
}: {
  access: TestingAccess
  vm: TestProductVm
  actions: TestProductActions
  kind?: TestingKind
}) {
  return (
    <TestingGate access={access}>
      {vm.product === null || vm.draft === null ? (
        <QueryState query={vm.query} emptyText="Товар не найден" onRetry={actions.reload} />
      ) : (
        // Черновик полей начинается заново с каждой версией товара: сохранили — видно то, что записал сервер.
        <ProductBody key={`${vm.product.id}:${vm.product.version}`} vm={vm} product={vm.product} draft={vm.draft} actions={actions} kind={kind} />
      )}
    </TestingGate>
  )
}

function ProductBody({
  vm,
  product,
  draft: initial,
  actions,
  kind,
}: {
  vm: TestProductVm
  product: TestProduct
  draft: TestProductDraft
  actions: TestProductActions
  kind: TestingKind
}) {
  const [draft, setDraft] = useState(initial)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const busy = vm.action.busy
  const changed = draft.name !== initial.name || draft.sku !== initial.sku || draft.barcodes !== initial.barcodes

  return (
    <section aria-labelledby="platform-testing-product-title" className="platform-testing">
      <header className="platform-resources__toolbar">
        <h2 id="platform-testing-product-title">{product.name}</h2>
        {vm.index !== null && <StateBadge badge={vm.index} />}
      </header>
      {vm.sourceText !== null && <p className="platform-testing__note">{vm.sourceText}. Правки здесь каталог не меняют.</p>}
      {kind === 'text' ? (
        <ol className="platform-testing__howto" aria-label="Как проверить товар">
          <li>
            <strong>Поля</strong> — по названию, артикулу и штрихкодам товар ищут запросы. Поправили — сохраните.
          </li>
          <li>
            <strong>Запросы</strong> («← Назад») — «Сгенерировать» сделает запросы из этих полей, свои добавляются там же.
          </li>
        </ol>
      ) : (
        <ol className="platform-testing__howto" aria-label="Как проверить товар">
          <li>
            <strong>Эталоны</strong> — фото товара, по которым поиск его узнаёт. При копировании из каталога все фото товара из 1С становятся эталонами сами,
            вручную — только если там фото нет.
          </li>
          <li>
            <strong>Кадры</strong> — 2–3 снимка, какими их сделает продавец: под углом, с бликом, в руке.
          </li>
          <li>
            <strong>Проверить</strong> у кадра покажет, на каком месте нашёлся товар. Все кадры всех товаров разом — «Замеры».
          </li>
        </ol>
      )}
      <ActionError error={vm.action.error} onReload={actions.reload} />

      <section aria-label="Поля товара">
        <h3>Поля товара</h3>
        <p className="platform-testing__note">По названию ищет текстовый поиск, по штрихкоду — сканер. Эти поля видит только тестовый индекс.</p>
        <div className="platform-testing__fields">
          <TextField label="Название" value={draft.name} onChange={(name) => setDraft({ ...draft, name })} />
          <TextField label="Артикул" value={draft.sku} onChange={(sku) => setDraft({ ...draft, sku })} />
          <TextField label="Штрихкоды" placeholder="через запятую" value={draft.barcodes} onChange={(barcodes) => setDraft({ ...draft, barcodes })} />
        </div>
        <div className="platform-testing__row platform-testing__actions">
          <Button
            variant="primary"
            icon={<Save size={16} aria-hidden="true" />}
            disabled={busy || draft.name.trim() === '' || !changed}
            disabledReason={draft.name.trim() === '' ? 'Нужно название' : changed ? null : 'Изменений нет — всё сохранено'}
            onClick={() => actions.save(draft)}
            actionId="platform.testing-product.save"
          >
            Сохранить
          </Button>
          {product.source !== null && (
            <Button
              icon={<RefreshCw size={16} aria-hidden="true" />}
              disabled={busy}
              onClick={actions.refreshFromSource}
              actionId="platform.testing-product.refresh"
            >
              Обновить из источника
            </Button>
          )}
          <Button
            variant="ghost"
            icon={<DatabaseZap size={16} aria-hidden="true" />}
            disabled={busy}
            onClick={actions.reindex}
            actionId="platform.testing-product.reindex"
          >
            Переиндексировать
          </Button>
          {confirmDelete ? (
            <>
              <Button variant="danger" icon={<Trash2 size={16} aria-hidden="true" />} onClick={actions.remove} actionId="platform.testing-product.delete">
                Подтвердить удаление
              </Button>
              <Button variant="ghost" onClick={() => setConfirmDelete(false)} actionId="platform.testing-product.delete">
                Не удалять
              </Button>
            </>
          ) : (
            <Button
              variant="ghost"
              icon={<Trash2 size={16} aria-hidden="true" />}
              onClick={() => setConfirmDelete(true)}
              actionId="platform.testing-product.delete"
            >
              Удалить товар
            </Button>
          )}
        </div>
      </section>

      {kind === 'photo' && (
        <>
          <section aria-label="Эталоны">
            <h3>Эталоны ({vm.references.length})</h3>
            <p className="platform-testing__note">Фото товара в тестовом индексе — по ним поиск узнаёт товар. Без эталона по фото товар не найти.</p>
            {vm.sourcePhotosNote !== null && (
              <p className="platform-testing__warning" role="status">
                <TriangleAlert size={16} aria-hidden="true" /> {vm.sourcePhotosNote}
              </p>
            )}
            {vm.references.length > 0 && <PhotoList photos={vm.references} name={product.name} busy={busy} actions={actions} checkable={false} />}
            <AddPhoto role="reference" busy={busy} onAdd={actions.addPhoto} />
          </section>

          <section aria-label="Кадры">
            <h3>Кадры ({vm.frames.length})</h3>
            <p className="platform-testing__note">Снимки, которыми проверяем поиск. В индекс они не попадают. Без кадров замер этот товар не проверит.</p>
            {vm.frames.length > 0 && <PhotoList photos={vm.frames} name={product.name} busy={busy} actions={actions} checkable />}
            <AddPhoto role="frame" busy={busy} onAdd={actions.addPhoto} />
          </section>
        </>
      )}
    </section>
  )
}

function PhotoList({
  photos,
  name,
  busy,
  actions,
  checkable,
}: {
  photos: readonly TestPhotoVm[]
  name: string
  busy: boolean
  actions: TestProductActions
  checkable: boolean
}) {
  return (
    <ul className="platform-testing__photos">
      {photos.map((photo, index) => (
        <li key={photo.id}>
          <ProductThumb src={photo.url} name={`${name}, ${checkable ? 'кадр' : 'эталон'} ${index + 1}`} size={120} />
          <small>{photo.originText}</small>
          {checkable && <small>{photo.tagsText}</small>}
          <div className="platform-testing__row">
            {checkable && (
              <Button
                variant="ghost"
                icon={<ScanSearch size={16} aria-hidden="true" />}
                aria-label={`Проверить кадр ${index + 1}`}
                onClick={() => actions.checkFrame(photo.id)}
                actionId="platform.testing-product.check-frame"
              >
                Проверить
              </Button>
            )}
            <Button
              variant="ghost"
              icon={<Trash2 size={16} aria-hidden="true" />}
              aria-label={`Удалить ${checkable ? 'кадр' : 'эталон'} ${index + 1}`}
              disabled={busy}
              onClick={() => actions.removePhoto(photo.id)}
              actionId="platform.testing-product.delete-photo"
            >
              Удалить
            </Button>
          </div>
        </li>
      ))}
    </ul>
  )
}

/** Добавить эталон или кадр. Пометки кадра — для срезов замера («сбоку», «блик»); у эталона их нет. */
function AddPhoto({ role, busy, onAdd }: { role: 'reference' | 'frame'; busy: boolean; onAdd: TestProductActions['addPhoto'] }) {
  const [file, setFile] = useState<File | null>(null)
  const [tags, setTags] = useState<Record<string, string>>({})
  const [inputKey, setInputKey] = useState(0)
  // Подготовка фото (размер, JPEG) идёт до отправки: пока она идёт, второе нажатие не отправит тот же файл ещё раз.
  const [preparing, setPreparing] = useState(false)
  const noun = role === 'frame' ? 'кадр' : 'эталон'

  // Фото готовится так же, как в боевом поиске (размер, поворот, JPEG): замер мерит тот же путь.
  const add = async () => {
    if (file === null || preparing) return
    setPreparing(true)
    try {
      onAdd(role, await preparePhoto(file), role === 'frame' ? tags : {})
    } finally {
      setPreparing(false)
    }
    setFile(null)
    setTags({})
    setInputKey((key) => key + 1)
  }

  return (
    <div className="platform-testing__add" aria-label={`Добавить ${noun}`} role="group">
      <ActionInfo id="platform.testing-product.photo-file" label={`Файл: ${noun}`}>
        <label className="krp-field">
          <span className="krp-field__label">{role === 'frame' ? 'Файл кадра' : 'Файл эталона'}</span>
          <input key={inputKey} type="file" accept="image/*" onChange={(event) => setFile(event.target.files?.[0] ?? null)} />
        </label>
      </ActionInfo>
      {role === 'frame' && (
        <ActionInfo id="platform.testing-product.frame-tags" label="Пометки кадра" layout="block">
          <details className="platform-testing__tags-box">
            <summary>Пометки кадра — необязательно, нужны, чтобы замер показал, где поиск ошибается (например, только «с бликом»)</summary>
            <div className="platform-testing__tags">
              {TESTING_TAG_AXES.map((axis) => (
                <TextField
                  key={axis}
                  label={TAG_TITLES[axis] ?? axis}
                  placeholder={TAG_HINTS[axis] ?? ''}
                  value={tags[axis] ?? ''}
                  onChange={(value) => setTags({ ...tags, [axis]: value })}
                />
              ))}
            </div>
          </details>
        </ActionInfo>
      )}
      <div className="platform-testing__actions">
        <Button
          icon={<ImagePlus size={16} aria-hidden="true" />}
          disabled={busy || preparing || file === null}
          disabledReason={file === null ? 'Сначала выберите файл' : null}
          onClick={() => void add()}
          actionId="platform.testing-product.add-photo"
        >
          {role === 'frame' ? 'Добавить кадр' : 'Добавить эталон'}
        </Button>
      </div>
    </div>
  )
}
